import { useDeferredValue, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  ContentCard,
  LoadingSkeleton,
  Pagination,
  Row,
  Table,
  Tooltip,
} from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import {
  useFetchNachaPassthroughEntries,
  useFetchNachaPassthroughSubmission,
} from "byzantine/src/dbbl/businessLogic/entities/nachaPassthrough";
import Filters from "byzantine/src/filters";
import PageLayout from "../../../src/components/PageLayout";
import { formatCurrencyFromCents } from "../../PaymentActivity/CreateBulk/passthroughFormat";
import headerStyles from "../../PaymentActivity/Bulk/BulkSelectReversalScreen.module.scss";
import { SearchBar } from "../../PaymentActivity/common";
import styles from "./NachaPassthroughPaymentsPage.module.scss";

const PAGE_SIZE = 10;

type SubmissionSummary = Pick<
  API.NachaPassthrough.Submission,
  | "title"
  | "payment_credit_count"
  | "payment_debit_count"
  | "payment_total_credit_amount"
  | "payment_total_debit_amount"
  | "batches"
>;

const NachaPassthroughPaymentsPage = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { submissionId } = useParams<{ submissionId: string }>();
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState<SubmissionSummary | null>(null);
  const [entries, setEntries] = useState<API.NachaPassthrough.Entry[]>([]);
  const [totalPayments, setTotalPayments] = useState(0);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [page, setPage] = useState(1);
  const {
    send: fetchSubmission,
    loading: isSummaryLoading,
    error: hasSummaryError,
  } = useFetchNachaPassthroughSubmission();
  const {
    send: fetchEntries,
    loading: isEntriesLoading,
    error: hasEntriesError,
  } = useFetchNachaPassthroughEntries();

  useEffect(() => {
    if (!submissionId) return undefined;
    let isActive = true;

    fetchSubmission(submissionId as API.NachaPassthrough.SubmissionId).then(
      (response) => {
        if (isActive && response) setSummary(response);
      }
    );

    return () => {
      isActive = false;
    };
  }, [fetchSubmission, submissionId]);

  useEffect(() => {
    if (!submissionId) return undefined;
    let isActive = true;

    fetchEntries(
      submissionId as API.NachaPassthrough.SubmissionId,
      (page - 1) * PAGE_SIZE,
      PAGE_SIZE,
      deferredSearch
    ).then((response) => {
      if (!isActive || !response) return;
      setEntries(response.results);
      setTotalPayments(response.count);
    });

    return () => {
      isActive = false;
    };
  }, [deferredSearch, fetchEntries, page, submissionId]);

  const hasError = hasSummaryError || hasEntriesError;
  const paymentCount = summary
    ? summary.payment_credit_count + summary.payment_debit_count
    : 0;
  const netTotal = summary
    ? summary.payment_total_debit_amount - summary.payment_total_credit_amount
    : 0;
  const totalPages = Math.ceil(totalPayments / PAGE_SIZE);
  const handleBack = () => {
    const returnTo = searchParams.get("returnTo");
    const isValidReturnTo =
      returnTo?.match(/^\/approvals\/(pending|history)\/[^/]+$/) ||
      returnTo?.match(
        /^\/payment_activity\/file-import(?:\/ach-passthrough\/[^/?]+)?(?:\?.*)?$/
      );
    navigate(
      returnTo && isValidReturnTo ? returnTo : "/payment_activity/file-import"
    );
  };

  return (
    <LoadingSkeleton
      isLoading={isSummaryLoading || (!summary && isEntriesLoading)}
    >
      <PageLayout>
        <div>
          <div className={cc(headerStyles.header, "margin--top--xl")}>
            <div className={headerStyles.backButton}>
              <Button
                kind="plain"
                onClick={handleBack}
                testId="back-button"
                ariaLabel={l10n.getString("aria-label-back")}
                startIcon="chevron-left"
              />
            </div>
            <h1 className="fontColor--heading">
              {l10n.getString("ach-passthrough-payments-page-title", {
                title: summary?.title ?? "",
              })}
            </h1>
          </div>

          {summary ? (
            <div className="margin--top--l">
              <ContentCard kind="bordered" paddingSize="none" radiusSize="l">
                <Row
                  alignItems="center"
                  justifyContent="space-between"
                  gapSize="l"
                  className={styles.summaryCard}
                >
                  <Row.Item shrink>
                    <Row alignItems="center" gapSize="l">
                      <Row.Item shrink>
                        <div
                          className={`${styles.summaryItem} ${styles.totalPayments}`}
                        >
                          <h5 className="fontSize--xs fontWeight--bold fontColor--secondary">
                            {l10n
                              .getString("ach-passthrough-payments-total")
                              .toUpperCase()}
                          </h5>
                          <span className="fontSize--l fontWeight--bold">
                            {paymentCount}
                          </span>
                        </div>
                      </Row.Item>
                      <Row.Item shrink>
                        <div className={styles.divider} />
                      </Row.Item>
                      <Row.Item shrink>
                        <div className={styles.summaryItem}>
                          <h5 className="fontSize--xs fontWeight--bold fontColor--secondary">
                            {l10n.getString("label-net-total").toUpperCase()}
                          </h5>
                          <span className="fontSize--l fontWeight--bold">
                            {formatCurrencyFromCents(netTotal)}
                          </span>
                        </div>
                      </Row.Item>
                    </Row>
                  </Row.Item>
                  <Row.Item shrink>
                    <Row alignItems="center" gapSize="l">
                      <Row.Item shrink>
                        <div className={styles.summaryItem}>
                          <h5 className="fontSize--xs fontWeight--bold fontColor--secondary">
                            {l10n
                              .getString(
                                "ach-passthrough-payments-send-total",
                                {
                                  count: summary.payment_credit_count,
                                }
                              )
                              .toUpperCase()}
                          </h5>
                          <span className="fontSize--l fontWeight--bold">
                            {formatCurrencyFromCents(
                              summary.payment_total_credit_amount
                            )}
                          </span>
                        </div>
                      </Row.Item>
                      <Row.Item shrink>
                        <div className={styles.divider} />
                      </Row.Item>
                      <Row.Item shrink>
                        <div className={styles.summaryItem}>
                          <h5 className="fontSize--xs fontWeight--bold fontColor--secondary">
                            {l10n
                              .getString(
                                "ach-passthrough-payments-collect-total",
                                {
                                  count: summary.payment_debit_count,
                                }
                              )
                              .toUpperCase()}
                          </h5>
                          <span className="fontSize--l fontWeight--bold fontColor--success">
                            {formatCurrencyFromCents(
                              summary.payment_total_debit_amount
                            )}
                          </span>
                        </div>
                      </Row.Item>
                    </Row>
                  </Row.Item>
                </Row>
              </ContentCard>
            </div>
          ) : null}

          <div className="margin--top--m margin--bottom--m">
            <SearchBar
              searchValue={search}
              handleSearchChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder={l10n.getString(
                "ach-passthrough-payments-search-placeholder"
              )}
            />
          </div>

          {hasError ? (
            <div className="margin--top--xl">
              <Alert isActive isDismissable={false} kind="error">
                {l10n.getString("error-generic")}
              </Alert>
            </div>
          ) : null}

          {!hasError && !isEntriesLoading && entries.length === 0 ? (
            <div className={styles.message}>
              {l10n.getString("ach-passthrough-payments-empty")}
            </div>
          ) : null}

          {!hasError && entries.length ? (
            <Table
              colVisibility={[
                "*",
                "*",
                "*",
                "*",
                "*",
                "*",
                "*",
                "*",
                "*",
                "*",
                "*",
                "*",
              ]}
              colLayout={{
                s: "92px 229px 121px 157px 180px 214px 146px 225px 214px 214px 314px 119px",
                m: "92px 229px 121px 157px 180px 214px 146px 225px 214px 214px 314px 119px",
                l: "92px 229px 121px 157px 180px 214px 146px 225px 214px 214px 314px 119px",
              }}
              pinColumns="end"
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {l10n.getString("ach-passthrough-payments-batch-number")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("label-to-from")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    <div className={styles.headerWithTooltip}>
                      <span className={styles.headerWithTooltipLabel}>
                        {l10n.getString("label-action")}
                      </span>
                      <Tooltip
                        maxWidth="300px"
                        text={l10n.getString(
                          "ach-passthrough-payments-action-tooltip"
                        )}
                        wrapperDisplay="flex"
                      >
                        <span className="narmi-icon-info"></span>
                      </Tooltip>
                    </div>
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("ach-payment-sec-code")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("label-entry-description")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("label-company-id")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString(
                      "ach-passthrough-validation-check-effective-date"
                    )}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("ach-passthrough-payments-data")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("label-account")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("ach-passthrough-payments-rdfi")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("ach-passthrough-payments-trace-number")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {l10n.getString("label-amount")}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {entries.map((entry) => {
                  const batch = summary?.batches.find(
                    ({ id }) => id === entry.batch_id
                  );
                  const isSend = entry.direction === "credit";

                  return (
                    <Table.Row key={entry.id}>
                      <Table.Cell>{entry.batch_number}</Table.Cell>
                      <Table.Cell>
                        <span className={styles.recipientName}>
                          {entry.counterparty_name}
                        </span>
                        <span
                          className={cc(
                            styles.accountNumber,
                            "fontSize--s fontColor--secondary"
                          )}
                        >
                          {entry.masked_counterparty_account_number}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {isSend
                          ? l10n.getString(
                              "ach-passthrough-payments-action-send"
                            )
                          : l10n.getString(
                              "ach-passthrough-payments-action-collect"
                            )}
                      </Table.Cell>
                      <Table.Cell>{batch?.sec_code ?? "-"}</Table.Cell>
                      <Table.Cell>{batch?.entry_description ?? "-"}</Table.Cell>
                      <Table.Cell>{batch?.company_id ?? "-"}</Table.Cell>
                      <Table.Cell>
                        {batch?.effective_date
                          ? Filters.longMonthDay(batch.effective_date)
                          : "-"}
                      </Table.Cell>
                      <Table.Cell>
                        {batch?.discretionary_data || "-"}
                      </Table.Cell>
                      <Table.Cell>
                        {entry.masked_counterparty_account_number}
                      </Table.Cell>
                      <Table.Cell>
                        {entry.counterparty_routing_number.slice(0, 8)}
                      </Table.Cell>
                      <Table.Cell>{entry.trace_number}</Table.Cell>
                      <Table.Cell>
                        {formatCurrencyFromCents(
                          isSend ? -entry.amount : entry.amount
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          ) : null}

          {totalPages > 1 ? (
            <div className={styles.pagination}>
              <Pagination
                totalPages={totalPages}
                selectedPage={page}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>
      </PageLayout>
    </LoadingSkeleton>
  );
};

export default NachaPassthroughPaymentsPage;
